import config from '../../configLoader';

/**
 * Payroll Timesheet Edit (M2-B) — 10-key clock/duration parsing and tenant-zone
 * time stamping. Ported from the accepted design v2 bundle
 * (design_handoff_payroll_timesheet_edit_design_v2/app/payroll-edit-data.js);
 * the parse rules there are the spec:
 *
 *   parseClock: 1500→3:00p · 730→7:30a · 3→3:00p · 1215p→12:15p · 7a→7:00a
 *   No meridiem + hour 1–6 → PM (fixed day-shift heuristic — closed decision;
 *   explicit a/p or 24h always overrides; the echo line is the safety net).
 *
 *   parseDur: "8:30"→510 · "8.5"→510 · "8"→480 (≤24 = hours) · "480"→480 (>24 = minutes)
 *
 * Offsets: payroll-entered punch times are stamped with the TENANT timezone
 * offset, DST-correct per date — never the browser locale (plan decision 9;
 * `config.payrollTimesheetEditing.timeZone`, WCDP `America/Los_Angeles`).
 */

// ── 10-key parsers (spec-exact ports) ────────────────────────────────────────

/** Digits-first clock parser → minutes after midnight, or null on parse failure. */
export function parseClock(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  let s = String(raw).trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  let mer: string | null = null;
  const mm = s.match(/([ap])m?$/);
  if (mm) {
    mer = mm[1];
    s = s.slice(0, mm.index);
  }
  s = s.replace(/[^\d]/g, '');
  if (!s) return null;
  let h: number;
  let mi: number;
  if (s.length <= 2) { h = +s; mi = 0; }
  else if (s.length === 3) { h = +s[0]; mi = +s.slice(1); }
  else { h = +s.slice(0, 2); mi = +s.slice(2, 4); }
  if (mi > 59 || h > 23) return null;
  if (mer === 'p') { if (h < 12) h += 12; }
  else if (mer === 'a') { if (h === 12) h = 0; }
  else if (h >= 1 && h <= 6) h += 12; // fixed 1–6 → PM day-shift heuristic
  return h * 60 + mi;
}

/** The editor's refusal for a punch that runs past midnight — the API's exact text (all tenants). */
export const PUNCH_CROSSES_MIDNIGHT_MESSAGE =
  "A punch can't cross midnight. End it at 12:00 AM and add the rest on the next day starting at 12:00 AM.";
/** The `code` the API's 400 carries for the same refusal (admin/timesheetEdit routes). */
export const PUNCH_CROSSES_MIDNIGHT_CODE = 'PUNCH_CROSSES_MIDNIGHT';

/**
 * In/out clock minutes (parseClock) → the out punch's minutes relative to the IN day.
 * An out earlier than the in rolls to the next day, so 10:00p → 12:00a is 1440 (sent
 * as next-day 00:00, which the API accepts). Anything past that — 10:00p → 3:00a
 * (1620) — crosses midnight: payroll must end the punch at 12:00 AM and add the rest
 * on the next day starting at 12:00 AM.
 */
export function resolveOutMinutes(inMinutes: number, outMinutes: number): { minutes: number; crossesMidnight: boolean } {
  const minutes = outMinutes >= inMinutes ? outMinutes : outMinutes + 1440;
  return { minutes, crossesMidnight: minutes > 1440 };
}

/** Duration parser → minutes, or null. `8:30`→510 · `8.5`→510 · `8`→480 · `480`→480. */
export function parseDur(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).trim().toLowerCase().replace(/[hm\s]/g, '');
  if (!s) return null;
  if (s.includes(':')) {
    const [a, b] = s.split(':');
    const mi = +(b || 0);
    if (Number.isNaN(+a) || Number.isNaN(mi) || mi > 59) return null;
    return (+a) * 60 + mi;
  }
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  if (s.includes('.')) return Math.round(n * 60);
  return n <= 24 ? n * 60 : Math.round(n);
}

/** Loose money/amount parser → number, or null. Strips $ , etc. */
export function parseAmt(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const s = String(raw).replace(/[^0-9.-]/g, '');
  if (!s) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
}

// ── Compact clock formatting (`7:30a` — closed decision 4) ───────────────────

/** Minutes-after-midnight → compact `7:30a` clock (wraps past 24h). */
export function formatClockMinutes(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const h24 = Math.floor(m / 60);
  const mi = m % 60;
  const ap = h24 >= 12 ? 'p' : 'a';
  let h = h24 % 12;
  if (h === 0) h = 12;
  return `${h}:${String(mi).padStart(2, '0')}${ap}`;
}

// ── Punch-local wall time (the SWITCHOFFSET convention) ──────────────────────

const OFFSET_RE = /^[+-]\d{2}:\d{2}$/;

/** `-07:00` → signed minutes. Invalid/absent offsets read as 0 (UTC). */
export function offsetStringToMinutes(offset: string | null | undefined): number {
  if (!offset || !OFFSET_RE.test(offset)) return 0;
  const sign = offset.startsWith('-') ? -1 : 1;
  const h = parseInt(offset.slice(1, 3), 10);
  const m = parseInt(offset.slice(4, 6), 10);
  return sign * (h * 60 + m);
}

export interface LocalWallTime {
  /** `YYYY-MM-DD` local work date. */
  ymd: string;
  /** Minutes after local midnight. */
  minutes: number;
}

/**
 * A stored punch instant + its true local offset → the local wall date and
 * minutes (SWITCHOFFSET(InDateTime, InDateTimeOffset) then read as-is).
 */
export function instantToLocalWallTime(
  iso: string | null | undefined,
  offset: string | null | undefined
): LocalWallTime | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const shifted = new Date(t + offsetStringToMinutes(offset) * 60000);
  const ymd = shifted.toISOString().slice(0, 10);
  const minutes = shifted.getUTCHours() * 60 + shifted.getUTCMinutes();
  return { ymd, minutes };
}

// ── Tenant-zone stamping (decision 9) ────────────────────────────────────────

/** The tenant IANA zone; browser zone only when the config block is absent. */
export function tenantTimeZone(): string {
  return (
    config.payrollTimesheetEditing?.timeZone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
}

/** Signed offset minutes of `zone` at the given UTC instant (DST-correct). */
export function zoneOffsetMinutesAt(instantMs: number, zone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(new Date(instantMs));
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);
  // hourCycle quirks: "24" for midnight under some ICU versions.
  const hour = get('hour') % 24;
  const asUTC = Date.UTC(get('year'), get('month') - 1, get('day'), hour, get('minute'), get('second'));
  return Math.round((asUTC - instantMs) / 60000);
}

/** Signed minutes → `[+-]HH:MM`. */
export function minutesToOffsetString(minutes: number): string {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export interface StampedInstant {
  /** UTC ISO string — unambiguous instant for the request body. */
  iso: string;
  /** The tenant-zone `[+-]HH:MM` offset at that instant. */
  offset: string;
}

/**
 * A local wall time (`ymd` + minutes after midnight, which may exceed 1440 for
 * an overnight out-punch) in the TENANT zone → the instant + offset the CRUD
 * body carries. Two-pass so the offset is DST-correct for the wall date itself.
 */
export function wallTimeToTenantInstant(ymd: string, minutes: number): StampedInstant {
  const zone = tenantTimeZone();
  const [y, mo, d] = ymd.split('-').map((x) => parseInt(x, 10));
  const wallUTC = Date.UTC(y, mo - 1, d, 0, minutes, 0);
  // First guess: treat the wall time as UTC, then correct by the zone offset —
  // twice, so a punch inside a DST transition converges on the right offset.
  let offsetMin = zoneOffsetMinutesAt(wallUTC, zone);
  let instant = wallUTC - offsetMin * 60000;
  offsetMin = zoneOffsetMinutesAt(instant, zone);
  instant = wallUTC - offsetMin * 60000;
  return { iso: new Date(instant).toISOString(), offset: minutesToOffsetString(offsetMin) };
}

// ── Small shared formatters ──────────────────────────────────────────────────

/** `$1,234.56`. */
export function money(v: number): string {
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** `2026-08-03` (or ISO datetime) → `Wednesday` / `Wed` / `08/03` pieces. */
export function ymdParts(ymd: string): { weekday: string; short: string; md: string } {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  return {
    weekday: d.toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' }),
    short: d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }),
    md: `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`,
  };
}

/** `Wed Aug 5`-style label for modal subtitles and delete confirms. */
export function ymdLabel(ymd: string): string {
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
